import { Plus, RefreshCcw, Webhook } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useTranslations } from "@/shared/lang/useTranslations";

import { useAccessCan } from "../../hooks/useAccessCan";
import { useOrganizationWebhookDeliveries } from "../../hooks/useOrganizationWebhookDeliveries";
import { useOrganizationWebhooksData } from "../../hooks/useOrganizationWebhooksData";
import {
  DEFAULT_SECRET_HEADER,
  type OrganizationWebhookDelivery,
} from "../../services/organizationWebhooksService";
import { SettingsSection } from "../SettingsSection";
import { WebhookConfigCard } from "./WebhookConfigCard";
import { WebhookDeliveriesFilters } from "./WebhookDeliveriesFilters";
import { WebhookDeliveriesTable } from "./WebhookDeliveriesTable";
import { WebhookDeliveryDrawer } from "./WebhookDeliveryDrawer";
import { WebhookIntegrationGuide } from "./WebhookIntegrationGuide";
import { WebhookSecretBanner } from "./WebhookSecretBanner";
import { WebhookTestResultAlert } from "./WebhookTestResultAlert";
import { WebhookUrlDialog } from "./WebhookUrlDialog";

export const WEBHOOKS_SECTION_ID = "webhooks";

/**
 * Settings → Developers → Webhooks, for organization members. Three states of the endpoint read: loading
 * (skeleton), none configured (an invitation to add one), configured (the receiver console + the delivery
 * report). The report is only fetched once an endpoint exists — there is nothing to list before.
 *
 * Write affordances are gated optimistically on `organization_webhooks:write` / `:delete`; the backend 403
 * (surfaced as a toast on the mutation) stays the boundary, like everywhere else in Settings.
 */
export function WebhooksSection() {
  const { t } = useTranslations("settings");
  const { canAll } = useAccessCan();
  const canWrite = canAll("organization_webhooks:write");
  const canDelete = canAll("organization_webhooks:delete");

  const {
    config,
    isLoading,
    hasError,
    reload,
    isSubmitting,
    revealedSecret,
    dismissSecret,
    fullSecret,
    isRevealingSecret,
    revealSecret,
    hideFullSecret,
    testResult,
    clearTestResult,
    saveEndpoint,
    setActive,
    rotateSecret,
    removeEndpoint,
    sendTest,
  } = useOrganizationWebhooksData();

  const deliveries = useOrganizationWebhookDeliveries(config !== null);

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [openDelivery, setOpenDelivery] = useState<OrganizationWebhookDelivery | null>(null);

  const handleSendTest = async () => {
    const result = await sendTest();
    // The ping is logged as a delivery, so the report should show it without a manual refresh.
    if (result) deliveries.refresh();
  };

  const handleRotateSecret = async () => {
    const done = await rotateSecret();
    // The backend re-verifies with the new secret, which logs a delivery of its own.
    if (done) deliveries.refresh();
    return done;
  };

  const handleRetry = async (id: string) => {
    const updated = await deliveries.retry(id);
    if (updated) setOpenDelivery(updated);
  };

  return (
    <SettingsSection
      id={WEBHOOKS_SECTION_ID}
      eyebrow={t("webhooks_section_eyebrow")}
      title={t("webhooks_section_title")}
      sub={t("webhooks_section_sub")}
      headerRight={
        config && (
          <ColocaButton
            variant="ghost"
            size="sm"
            icon={RefreshCcw}
            loading={deliveries.isRefreshing}
            onClick={() => {
              void reload();
              deliveries.refresh();
            }}
          >
            {t("webhooks_deliveries_refresh")}
          </ColocaButton>
        )
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          <ColocaSkeleton className="h-[72px] w-full" />
          <ColocaSkeleton className="h-[44px] w-full" />
          <ColocaSkeleton className="h-[64px] w-full" />
        </div>
      ) : hasError ? (
        <ColocaAlert
          variant="destructive"
          text={t("webhooks_load_error")}
          inline
          actions={
            <ColocaButton size="sm" variant="default" onClick={() => void reload()}>
              {t("webhooks_retry_load")}
            </ColocaButton>
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          {!canWrite && <ColocaAlert variant="secondary" text={t("webhooks_read_only_notice")} inline />}
          {config === null ? (
            <ColocaEmpty
              surface="card"
              icon={Webhook}
              title={t("webhooks_empty_title")}
              description={t("webhooks_empty_desc")}
            >
              <ColocaButton
                variant="primary"
                icon={Plus}
                onClick={() => setUrlDialogOpen(true)}
                disabled={!canWrite}
              >
                {t("webhooks_add_endpoint")}
              </ColocaButton>
            </ColocaEmpty>
          ) : (
            <>
              {revealedSecret && (
                <WebhookSecretBanner
                  secret={revealedSecret}
                  headerName={config.secret_header}
                  onDismiss={dismissSecret}
                />
              )}

              <WebhookConfigCard
                config={config}
                canWrite={canWrite}
                canDelete={canDelete}
                isSubmitting={isSubmitting}
                fullSecret={fullSecret}
                isRevealingSecret={isRevealingSecret}
                onRevealSecret={() => void revealSecret()}
                onHideSecret={hideFullSecret}
                onEditUrl={() => setUrlDialogOpen(true)}
                onToggleActive={(next) => void setActive(next)}
                onSendTest={() => void handleSendTest()}
                onRotateSecret={handleRotateSecret}
                onDelete={removeEndpoint}
              />

              {testResult && (
                <WebhookTestResultAlert
                  result={testResult}
                  secretHeader={config.secret_header}
                  onDismiss={clearTestResult}
                />
              )}
            </>
          )}

          {/* The contract, one gesture away from the buttons that trip it: the handshake echo
              and what rotating the secret or re-pointing the URL does to a live endpoint. It
              renders before an endpoint exists too — that is when it is read first. */}
          <WebhookIntegrationGuide secretHeader={config?.secret_header ?? DEFAULT_SECRET_HEADER} />

          {/* Delivery report */}
          {config !== null && (
            <div className="mt-2">
              <div className="mb-3">
                <h3 className="text-[14px] font-semibold text-foreground">
                  {t("webhooks_deliveries_title")}
                </h3>
                <p className="mt-0.5 text-[12.5px] text-muted-foreground">{t("webhooks_deliveries_sub")}</p>
              </div>
              <div className="mb-3">
                <WebhookDeliveriesFilters
                  filters={deliveries.filters}
                  eventTypes={deliveries.eventTypes}
                  hasActiveFilters={deliveries.hasActiveFilters}
                  isApplying={deliveries.isLoading}
                  onChange={deliveries.setFilters}
                  onClear={deliveries.clearFilters}
                />
              </div>
              <div className="overflow-hidden rounded-md border border-border">
                <WebhookDeliveriesTable
                  rows={deliveries.rows}
                  total={deliveries.total}
                  page={deliveries.page}
                  perPage={deliveries.perPage}
                  isLoading={deliveries.isLoading}
                  hasActiveFilters={deliveries.hasActiveFilters}
                  onPageChange={deliveries.setPage}
                  onOpen={setOpenDelivery}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <WebhookUrlDialog
        open={urlDialogOpen}
        currentUrl={config?.url}
        secretHeader={config?.secret_header ?? DEFAULT_SECRET_HEADER}
        isSubmitting={isSubmitting}
        onSubmit={async (input) => {
          const outcome = await saveEndpoint(input);
          // Every save that reaches the receiver logs a delivery — the handshake ping on a first
          // registration, the backend's re-verification on a re-point — so the report is stale either way.
          if (outcome.saved) deliveries.refresh();
          return outcome;
        }}
        onClose={() => setUrlDialogOpen(false)}
      />

      <WebhookDeliveryDrawer
        delivery={openDelivery}
        open={openDelivery !== null}
        canWrite={canWrite}
        isRetrying={deliveries.isRetrying}
        onRetry={(id) => void handleRetry(id)}
        onClose={() => setOpenDelivery(null)}
      />
    </SettingsSection>
  );
}
